/**
 * Access page (/access) — who can get into the cockpit, and how their access ends.
 *
 * This is the answer to "someone works here for a month and then leaves". Invite by
 * email, give guests an end date, and take access away with one click. Deactivating
 * kills every session that person holds, so they're out on their next click, not
 * whenever a token happens to expire.
 *
 * Admin only. The backend enforces that too (app/api/endpoints/auth.py); this page
 * just doesn't render for anyone else.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useTheme } from '../context/ThemeContext';
import {
  CockpitUser,
  inviteAccessUser,
  listAccessUsers,
  revokeAccessUserSessions,
  updateAccessUser,
} from '../services/auth';

interface Props {
  currentUser: CockpitUser | null;
}

const fmtDate = (value?: string | null): string => {
  if (!value) return '';
  try {
    return new Date(value).toLocaleDateString();
  } catch {
    return value;
  }
};

const daysLeft = (value?: string | null): number | null => {
  if (!value) return null;
  const end = new Date(value).getTime();
  if (Number.isNaN(end)) return null;
  return Math.ceil((end - Date.now()) / (1000 * 60 * 60 * 24));
};

const Access: React.FC<Props> = ({ currentUser }) => {
  const { theme } = useTheme();
  const [users, setUsers] = useState<CockpitUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<string | null>(null);

  const [email, setEmail] = useState('');
  const [role, setRole] = useState<'admin' | 'team' | 'guest'>('guest');
  const [days, setDays] = useState<string>('90');
  const [note, setNote] = useState('');
  const [inviting, setInviting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setUsers(await listAccessUsers());
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Could not load the access list.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (currentUser && currentUser.role !== 'admin') {
    return (
      <div className="container" style={{ color: theme.text }}>
        <h1 style={{ fontSize: '20px' }}>Access</h1>
        <p style={{ color: theme.textSecondary }}>
          Only an admin can manage who gets into the cockpit. Ask Joshua.
        </p>
      </div>
    );
  }

  const handleInvite = async (event: React.FormEvent) => {
    event.preventDefault();
    setInviting(true);
    setError(null);
    setFeedback(null);
    try {
      const parsedDays = parseInt(days, 10);
      const invited = await inviteAccessUser({
        email: email.trim(),
        role,
        expires_in_days: Number.isFinite(parsedDays) && parsedDays > 0 ? parsedDays : undefined,
        note: note.trim() || undefined,
      });
      setFeedback(
        `${invited.email} can now sign in. They'll get a code by email when they do.`
      );
      setEmail('');
      setNote('');
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Could not add that person.');
    } finally {
      setInviting(false);
    }
  };

  const setActive = async (user: CockpitUser, active: boolean) => {
    setError(null);
    setFeedback(null);
    try {
      await updateAccessUser(user.id, { active });
      setFeedback(
        active
          ? `${user.email} can sign in again.`
          : `${user.email} is out. Every session they held has been revoked.`
      );
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Could not change that person.');
    }
  };

  const extend = async (user: CockpitUser, extraDays: number) => {
    setError(null);
    setFeedback(null);
    try {
      await updateAccessUser(user.id, { expires_in_days: extraDays });
      setFeedback(`${user.email} now has ${extraDays} more days.`);
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Could not change the end date.');
    }
  };

  const signOut = async (user: CockpitUser) => {
    setError(null);
    setFeedback(null);
    try {
      const count = await revokeAccessUserSessions(user.id);
      setFeedback(`Signed ${user.email} out of ${count} browser session(s).`);
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Could not sign that person out.');
    }
  };

  const changeRole = async (user: CockpitUser, nextRole: 'admin' | 'team' | 'guest') => {
    setError(null);
    setFeedback(null);
    try {
      await updateAccessUser(user.id, { role: nextRole });
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Could not change that role.');
    }
  };

  const cardStyle: React.CSSProperties = {
    backgroundColor: theme.surface,
    border: `1px solid ${theme.border}`,
    borderRadius: '10px',
    padding: '20px',
    marginBottom: '20px',
  };

  const inputStyle: React.CSSProperties = {
    padding: '10px',
    border: `1px solid ${theme.border}`,
    borderRadius: '6px',
    backgroundColor: theme.background,
    color: theme.text,
    fontSize: '14px',
  };

  const smallButton = (color: string): React.CSSProperties => ({
    padding: '6px 10px',
    fontSize: '12px',
    borderRadius: '6px',
    border: `1px solid ${color}`,
    backgroundColor: 'transparent',
    color,
    cursor: 'pointer',
    marginRight: '6px',
  });

  return (
    <div className="container" style={{ color: theme.text }}>
      <h1 style={{ fontSize: '22px', marginBottom: '4px' }}>Access</h1>
      <p style={{ color: theme.textSecondary, fontSize: '14px', marginTop: 0 }}>
        Everyone who can sign in. People sign in with a code emailed to the address listed
        here, so removing someone here removes them everywhere.
      </p>

      {error && (
        <div style={{ ...cardStyle, borderColor: theme.error, color: theme.error }}>{error}</div>
      )}
      {feedback && !error && (
        <div style={{ ...cardStyle, borderColor: theme.success, color: theme.success }}>
          {feedback}
        </div>
      )}

      <form onSubmit={handleInvite} style={cardStyle}>
        <h2 style={{ fontSize: '16px', marginTop: 0 }}>Give someone access</h2>
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div style={{ flex: '2 1 260px' }}>
            <label style={{ display: 'block', fontSize: '12px', color: theme.textSecondary, marginBottom: '4px' }}>
              Email
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="applicant@example.com"
              style={{ ...inputStyle, width: '100%' }}
              required
            />
          </div>
          <div style={{ flex: '1 1 120px' }}>
            <label style={{ display: 'block', fontSize: '12px', color: theme.textSecondary, marginBottom: '4px' }}>
              Role
            </label>
            <select
              value={role}
              onChange={(e) => setRole(e.target.value as 'admin' | 'team' | 'guest')}
              style={{ ...inputStyle, width: '100%' }}
            >
              <option value="guest">Guest (applicant, contractor)</option>
              <option value="team">Team</option>
              <option value="admin">Admin</option>
            </select>
          </div>
          <div style={{ flex: '1 1 120px' }}>
            <label style={{ display: 'block', fontSize: '12px', color: theme.textSecondary, marginBottom: '4px' }}>
              Access ends in (days)
            </label>
            <input
              type="number"
              min={1}
              value={days}
              onChange={(e) => setDays(e.target.value)}
              placeholder="blank for no end date"
              style={{ ...inputStyle, width: '100%' }}
            />
          </div>
          <div style={{ flex: '2 1 200px' }}>
            <label style={{ display: 'block', fontSize: '12px', color: theme.textSecondary, marginBottom: '4px' }}>
              Note (optional)
            </label>
            <input
              type="text"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Revenue Challenge applicant"
              style={{ ...inputStyle, width: '100%' }}
            />
          </div>
          <button
            type="submit"
            disabled={inviting}
            style={{
              padding: '10px 18px',
              backgroundColor: theme.primary,
              color: '#FFFFFF',
              border: 'none',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: 600,
              cursor: inviting ? 'default' : 'pointer',
            }}
          >
            {inviting ? 'Adding...' : 'Add'}
          </button>
        </div>
      </form>

      <div style={cardStyle}>
        <h2 style={{ fontSize: '16px', marginTop: 0 }}>
          People ({users.filter((u) => u.active).length} active of {users.length})
        </h2>
        {loading ? (
          <p style={{ color: theme.textSecondary }}>Loading...</p>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <thead>
                <tr style={{ textAlign: 'left', color: theme.textSecondary }}>
                  <th style={{ padding: '8px 6px' }}>Person</th>
                  <th style={{ padding: '8px 6px' }}>Role</th>
                  <th style={{ padding: '8px 6px' }}>Access ends</th>
                  <th style={{ padding: '8px 6px' }}>Last seen</th>
                  <th style={{ padding: '8px 6px' }}>Signed in</th>
                  <th style={{ padding: '8px 6px' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => {
                  const left = daysLeft(user.expires_at);
                  const endingSoon = left !== null && left <= 14;
                  return (
                    <tr
                      key={user.id}
                      style={{
                        borderTop: `1px solid ${theme.border}`,
                        opacity: user.active ? 1 : 0.55,
                      }}
                    >
                      <td style={{ padding: '10px 6px' }}>
                        <div style={{ fontWeight: 600 }}>{user.name || '(no name yet)'}</div>
                        <div style={{ color: theme.textSecondary }}>{user.email}</div>
                        {user.note && (
                          <div style={{ color: theme.textSecondary, fontSize: '12px' }}>{user.note}</div>
                        )}
                      </td>
                      <td style={{ padding: '10px 6px' }}>
                        <select
                          value={user.role}
                          onChange={(e) =>
                            changeRole(user, e.target.value as 'admin' | 'team' | 'guest')
                          }
                          style={{ ...inputStyle, padding: '4px 6px', fontSize: '12px' }}
                        >
                          <option value="guest">guest</option>
                          <option value="team">team</option>
                          <option value="admin">admin</option>
                        </select>
                      </td>
                      <td style={{ padding: '10px 6px', color: endingSoon ? theme.warning : theme.text }}>
                        {user.expires_at ? `${fmtDate(user.expires_at)} (${left}d)` : 'no end date'}
                      </td>
                      <td style={{ padding: '10px 6px', color: theme.textSecondary }}>
                        {user.last_seen_at ? fmtDate(user.last_seen_at) : 'never'}
                      </td>
                      <td style={{ padding: '10px 6px', color: theme.textSecondary }}>
                        {user.active_sessions || 0}
                      </td>
                      <td style={{ padding: '10px 6px', whiteSpace: 'nowrap' }}>
                        {user.active ? (
                          <button style={smallButton(theme.error)} onClick={() => setActive(user, false)}>
                            Remove access
                          </button>
                        ) : (
                          <button style={smallButton(theme.success)} onClick={() => setActive(user, true)}>
                            Restore
                          </button>
                        )}
                        <button style={smallButton(theme.textSecondary)} onClick={() => signOut(user)}>
                          Sign out
                        </button>
                        <button style={smallButton(theme.primary)} onClick={() => extend(user, 30)}>
                          +30 days
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <p style={{ color: theme.textSecondary, fontSize: '12px' }}>
        Locked out with email down? On the box, run
        <code style={{ margin: '0 4px' }}>python3 scripts/manage_access.py login-link you@example.com</code>
        from the cockpit backend folder.
      </p>
    </div>
  );
};

export default Access;
